import { useEffect } from "react";
import { JOINT_FRAME_AXES_NAME } from "../../selected-joint-visibility";
import { createSelectedJointFrameMarkerGroup } from "../../selected-mesh-visibility/inspectorOverlays";
import { getSceneState } from "../../stores/sceneStore";
import { logger } from "../../utils/logger";
import { addSiteOrientationAxes, removeSiteOrientationAxes } from "../siteOrientationAxesOverlay";
import { devLog, EMPTY_OBJECT } from "../scene/sceneConstants";
import { getJointPositionSliderScreenPosition } from "../scene/sceneMisc";
import { getInertialSelectionKeyFromUserData, safeTraverse } from "../scene/sceneObjectLookups";
import { applyJointActuatorAccent } from "../scene/tendonStyle";

export function useSceneSelectionOverlays({
  actuatedJointUuids,
  actuatorDisplayLabelByJointUuid,
  actuatorSignature,
  cameraRef,
  getJointIdFromObject,
  highlightedJoint,
  inertialVisibilityUi,
  jointEntries,
  jointVisibilityUi,
  positionJointSliderNextToJoint,
  rendererRef,
  safeJointMapObject,
  sceneObjects,
  sceneRef,
  selectedInertial,
  selectedJoint,
  selectedSite,
  selectionActions,
  setHoverPreviewBody,
  setHoverPreviewJoint,
  setHoverPreviewMesh,
  setJointPositionSliderScreenPos,
  showActuators,
  showJointPositionSlider,
  showJoints,
  siteVisibilityUi,
}) {
  useEffect(() => {
    const handleBodyLabelClick = (event) => {
      const bodyUuid = event.detail?.bodyUuid;
      if (!bodyUuid) return;
      devLog("[Scene] Body label clicked:", bodyUuid);
      try {
        selectionActions.setLastSelectionSource("sceneClick");
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
      setHoverPreviewBody(null);
      setHoverPreviewJoint(null);
      setHoverPreviewMesh(null);
      selectionActions.selectBody(bodyUuid);
    };

    const handleJointLabelClick = (event) => {
      const jointUuid = event.detail.jointUuid;
      devLog("[Scene] Joint label clicked:", jointUuid);
      try {
        selectionActions.setLastSelectionSource("sceneClick");
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
      setHoverPreviewBody(null);
      setHoverPreviewJoint(null);
      setHoverPreviewMesh(null);
      if (showJointPositionSlider) {
        positionJointSliderNextToJoint(jointUuid);
      }
      selectionActions.selectJoint(jointUuid);
    };

    const handleSiteLabelClick = (event) => {
      const { siteUuid, siteName } = event.detail;
      devLog("[Scene] Site label clicked:", siteUuid || siteName);
      try {
        selectionActions.setLastSelectionSource("sceneClick");
      } catch (error) {
        logger.warn("[Scene] Unexpected error during scene update", error);
      }
      selectionActions.selectSite(siteUuid || siteName);
    };

    window.addEventListener("body-label-click", handleBodyLabelClick);
    window.addEventListener("joint-label-click", handleJointLabelClick);
    window.addEventListener("site-label-click", handleSiteLabelClick);

    return () => {
      window.removeEventListener("body-label-click", handleBodyLabelClick);
      window.removeEventListener("joint-label-click", handleJointLabelClick);
      window.removeEventListener("site-label-click", handleSiteLabelClick);
    };
  }, [
    positionJointSliderNextToJoint,
    selectionActions,
    setHoverPreviewBody,
    setHoverPreviewJoint,
    setHoverPreviewMesh,
    showJointPositionSlider,
  ]);

  useEffect(() => {
    if (!sceneRef.current) return;
    safeTraverse(sceneRef.current, (obj) => {
      if (obj.userData && obj.userData.isGlow) {
        obj.visible = false;
      }
    });
  }, [highlightedJoint, sceneRef, selectedJoint, showJoints]);

  useEffect(() => {
    if (!selectedJoint) return;
    setHoverPreviewJoint(null);
  }, [selectedJoint, setHoverPreviewJoint]);

  useEffect(() => {
    if (!sceneRef.current) return;
    const targetJoint = selectedJoint;
    safeTraverse(sceneRef.current, (obj) => {
      if (!obj || !obj.userData || !obj.userData.isJointVizGroup) return;
      const jointId = obj.userData.jointUuid;

      let center = obj.userData.centerSphere || null;
      if (!center) {
        let found = null;
        safeTraverse(obj, (child) => {
          if (
            !found &&
            child &&
            child.isMesh &&
            child.userData &&
            !child.userData.isGlow &&
            !child.userData.isJointEndpoint &&
            getJointIdFromObject(child) &&
            getJointIdFromObject(child) === jointId
          ) {
            found = child;
            return false;
          }
          return undefined;
        });
        center = found;
      }
      if (!center) return;

      if (showJoints) {
        center.visible = true;
      } else {
        center.visible = typeof targetJoint === "string" && jointId === targetJoint;
      }
    });
  }, [getJointIdFromObject, sceneObjects, sceneRef, selectedJoint, showJoints]);

  useEffect(() => {
    if (!sceneRef.current) return;

    safeTraverse(sceneRef.current, (obj) => {
      if (!obj?.userData?.isJointVizGroup) return;
      const existing = obj.getObjectByName(JOINT_FRAME_AXES_NAME);
      if (existing) obj.remove(existing);
    });

    if (!jointVisibilityUi.frameAxes || !selectedJoint) return;

    safeTraverse(sceneRef.current, (obj) => {
      if (!obj?.userData?.isJointVizGroup) return;
      if (obj.userData.jointUuid !== selectedJoint) return;
      const marker = createSelectedJointFrameMarkerGroup(0.12);
      marker.name = JOINT_FRAME_AXES_NAME;
      obj.add(marker);
      return false;
    });
  }, [jointEntries, jointVisibilityUi.frameAxes, sceneRef, selectedJoint]);

  useEffect(() => {
    const joint = selectedJoint ? getSceneState().joints?.[selectedJoint] : null;
    const isScalar = joint?.kind === "hinge" || joint?.kind === "slide";
    if (!showJointPositionSlider || !showJoints || !selectedJoint || !isScalar) {
      setJointPositionSliderScreenPos(null);
      return undefined;
    }

    let rafId = 0;
    const updatePosition = () => {
      const pos = getJointPositionSliderScreenPosition(
        sceneRef.current,
        cameraRef.current,
        rendererRef.current,
        selectedJoint
      );
      setJointPositionSliderScreenPos(pos || null);
      rafId = window.requestAnimationFrame(updatePosition);
    };
    updatePosition();
    return () => window.cancelAnimationFrame(rafId);
  }, [
    cameraRef,
    jointEntries,
    rendererRef,
    sceneRef,
    selectedJoint,
    setJointPositionSliderScreenPos,
    showJointPositionSlider,
    showJoints,
  ]);

  useEffect(() => {
    if (!sceneRef.current) return;

    safeTraverse(sceneRef.current, (obj) => {
      if (!obj?.userData?.isJointVizGroup || !obj.userData.jointUuid) return;
      const jointUuid = obj.userData.jointUuid;
      const hasActuator = actuatedJointUuids.has(jointUuid);
      const jointsMap = safeJointMapObject || EMPTY_OBJECT;
      const jointName =
        (jointsMap[jointUuid] && jointsMap[jointUuid].name) || obj.userData.jointName || "";
      const actuatorDisplayName = actuatorDisplayLabelByJointUuid.get(jointUuid) ?? "";
      applyJointActuatorAccent(obj, {
        showActuators,
        hasActuator,
        jointName,
        actuatorDisplayName,
      });
    });
  }, [
    actuatedJointUuids,
    actuatorDisplayLabelByJointUuid,
    actuatorSignature,
    jointEntries,
    safeJointMapObject,
    sceneObjects,
    sceneRef,
    showActuators,
    showJoints,
  ]);

  useEffect(() => {
    if (!sceneRef.current) return;

    safeTraverse(sceneRef.current, (obj) => {
      if (!obj?.userData || obj.userData.type !== "site" || !obj.isGroup) return;
      removeSiteOrientationAxes(obj);
    });

    if (!siteVisibilityUi.orientationAxes || !selectedSite) return;

    safeTraverse(sceneRef.current, (obj) => {
      if (
        obj?.userData?.type === "site" &&
        obj.isGroup &&
        obj.userData?.siteUuid === selectedSite
      ) {
        addSiteOrientationAxes(obj);
        return false;
      }
      return undefined;
    });
  }, [sceneObjects, sceneRef, selectedSite, siteVisibilityUi.orientationAxes]);

  useEffect(() => {
    if (!sceneRef.current) return;

    safeTraverse(sceneRef.current, (obj) => {
      if (!obj?.userData || obj.userData.type !== "inertial" || !obj.isGroup) return;
      removeSiteOrientationAxes(obj);
    });

    if (!inertialVisibilityUi.orientationAxes || !selectedInertial) return;

    safeTraverse(sceneRef.current, (obj) => {
      if (!obj?.userData || obj.userData.type !== "inertial" || !obj.isGroup) return;
      const key = getInertialSelectionKeyFromUserData(obj.userData);
      if (key === selectedInertial) {
        addSiteOrientationAxes(obj);
        return false;
      }
      return undefined;
    });
  }, [inertialVisibilityUi.orientationAxes, sceneObjects, sceneRef, selectedInertial]);
}
